export type ReferenceSettlementPartyType = "customer" | "vendor";

export type UnadjustedEntryRow = {
  id: string;
  source_reference_no?: string;
  source_voucher_type?: string;
  voucher_date?: string;
  original_amount?: number | string;
  outstanding_amount?: number | string;
  credit_unregistered_amount?: number | string;
  debit_unregistered_amount?: number | string;
  status?: string;
  label?: string;
  value?: string | number;
  entry_type?: "debit" | "credit";
  entry_source?: "voucher_detail" | "unregistered_receipt";
};

export const pendingDueLookup = (partyType: ReferenceSettlementPartyType) =>
  partyType === "customer"
    ? "customer_unadjusted_debit_entries"
    : "vendor_unadjusted_credit_entries";

export const creditAvailableDebitLookup = (
  partyType: ReferenceSettlementPartyType
) =>
  partyType === "customer"
    ? "customer_unadjusted_credit_entries"
    : "vendor_unadjusted_debit_entries";

export const settlementColumnLabels = (partyType: ReferenceSettlementPartyType) =>
  partyType === "vendor"
    ? {
        leftTitle: "Credit Unadjusted (Received)",
        leftSearch: "Search vendor credits...",
        leftEmpty: "No vendor credit entries found.",
        rightTitle: "Debit Unadjusted (Paid)",
        rightSearch: "Search payments to vendor...",
        rightEmpty: "No payment references found.",
        selectError: "Select one vendor credit and one payment reference.",
      }
    : {
        leftTitle: "Debit Unadjusted (Paid)",
        leftSearch: "Search pending dues...",
        leftEmpty: "No pending dues found.",
        rightTitle: "Credit Unadjusted (Received)",
        rightSearch: "Search credit available...",
        rightEmpty: "No credit references found.",
        selectError: "Select one pending due and one credit reference.",
      };

export const normalizeUnadjustedRows = (resp: unknown): UnadjustedEntryRow[] => {
  const raw = Array.isArray(resp)
    ? resp
    : Array.isArray((resp as any)?.data)
      ? (resp as any).data
      : [];
  return raw.map((item: any) => {
    const entryType =
      item.entry_type === "debit" || item.entry_type === "credit"
        ? item.entry_type
        : parseMoney(item.credit_unregistered_amount) > 0.009
          ? "credit"
          : "debit";
    const outstanding =
      item.outstanding_amount ??
      item.original_amount ??
      (entryType === "credit"
        ? item.credit_unregistered_amount
        : item.debit_unregistered_amount);
    const voucherType = String(item.source_voucher_type ?? "");
    const isReceiptLike =
      voucherType === "invoice_receivables" || voucherType === "pur_payment";
    const isCreditNote =
      voucherType === "credit_note" ||
      voucherType.toLowerCase() === "creditnote";
    const isDebitNote =
      voucherType === "debit_note" ||
      voucherType.toLowerCase() === "debitnote";

    return {
      ...item,
      id: String(item.id ?? item.value ?? ""),
      entry_type: entryType,
      outstanding_amount: outstanding,
      original_amount: item.original_amount ?? outstanding,
      entry_source: isReceiptLike ? "unregistered_receipt" : "voucher_detail",
      source_voucher_type: isCreditNote
        ? "credit_note"
        : isDebitNote
          ? "debit_note"
          : item.source_voucher_type,
    };
  });
};

export const resolveEntryId = (row: UnadjustedEntryRow): string =>
  String(row.id ?? row.value ?? "");

export const parseMoney = (value: unknown): number => {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export const formatMoney = (value: number): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

export const formatVoucherType = (type?: string): string => {
  const key = String(type ?? "").toLowerCase();
  const map: Record<string, string> = {
    invoice_receivables: "Receipt voucher",
    pur_payment: "Payment voucher",
    journal_voucher: "Journal voucher",
    journal: "Journal voucher",
    cash_payment: "Cash payment voucher",
    cashpayment: "Cash payment voucher",
    bank_payment: "Bank payment voucher",
    bankpayment: "Bank payment voucher",
    cash_payment_voucher: "Cash payment voucher",
    bank_payment_voucher: "Bank payment voucher",
    petty_cash: "Petty cash voucher",
    manual_voucher: "Manual voucher",
    credit_note: "Credit note",
    creditnote: "Credit note",
    debit_note: "Debit note",
    debitnote: "Debit note",
  };
  return map[key] || (type ? type.replace(/_/g, " ") : "Voucher");
};

export const formatDisplayDate = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** Best-effort label when only a reference number is known (read-only settlement view). */
export const inferReferenceSubtitleFromNo = (refNo?: string): string => {
  const ref = String(refNo ?? "").trim().toUpperCase();
  if (ref.startsWith("JV")) return "Journal voucher";
  if (ref.startsWith("REC")) return "Unregistered receipt";
  if (ref.startsWith("CN")) return "Credit note";
  if (ref.startsWith("DN")) return "Debit note";
  if (ref.startsWith("PP") || ref.startsWith("PAY")) return "Payment voucher";
  return "Voucher";
};

export const resolveReferenceNo = (row: UnadjustedEntryRow): string =>
  String(
    row.source_reference_no ??
      row.label ??
      row.id ??
      ""
  ).trim();

export const resolveEntrySubtitle = (
  row: UnadjustedEntryRow,
  partyType: ReferenceSettlementPartyType
): string => {
  if (row.entry_source === "unregistered_receipt") {
    return partyType === "customer"
      ? "Unregistered receipt"
      : "Unregistered payment";
  }
  const voucherType = String(row.source_voucher_type ?? "").toLowerCase();
  if (voucherType === "credit_note" || voucherType === "creditnote") {
    return "Credit note";
  }
  if (voucherType === "debit_note" || voucherType === "debitnote") {
    return "Debit note";
  }
  return formatVoucherType(row.source_voucher_type);
};

export const resolveOutstanding = (row: UnadjustedEntryRow): number =>
  parseMoney(
    row.outstanding_amount ??
      row.original_amount ??
      (row.entry_type === "credit"
        ? row.credit_unregistered_amount
        : row.debit_unregistered_amount)
  );

export const filterEntries = (
  rows: UnadjustedEntryRow[],
  query: string,
  partyType: ReferenceSettlementPartyType = "customer"
): UnadjustedEntryRow[] => {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const haystack = [
      resolveReferenceNo(row),
      resolveEntrySubtitle(row, partyType),
      formatDisplayDate(row.voucher_date),
      String(row.outstanding_amount ?? ""),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const computeNetPosition = (
  creditTotal: number,
  debitTotal: number
): { label: string; tone: "credit" | "debit" | "neutral" } => {
  const net = creditTotal - debitTotal;
  if (Math.abs(net) < 0.009) {
    return { label: "Net position: balanced", tone: "neutral" };
  }
  if (net > 0) {
    return {
      label: `Net position: ${formatMoney(net)} in credit`,
      tone: "credit",
    };
  }
  return {
    label: `Net position: ${formatMoney(Math.abs(net))} pending due`,
    tone: "debit",
  };
};

export const buildSettlementEntryIds = (
  pendingDueRow: UnadjustedEntryRow | undefined,
  creditAvailableRow: UnadjustedEntryRow | undefined,
  partyType: ReferenceSettlementPartyType = "customer"
): { debitEntryId: string; creditEntryId: string } | { error: string } => {
  const labels = settlementColumnLabels(partyType);
  if (!pendingDueRow || !creditAvailableRow) {
    return { error: labels.selectError };
  }
  const pairError = validateSettlementPair(
    pendingDueRow,
    creditAvailableRow,
    partyType
  );
  if (pairError) {
    return { error: pairError };
  }

  return {
    debitEntryId: resolveEntryId(pendingDueRow),
    creditEntryId: resolveEntryId(creditAvailableRow),
  };
};

export const validateSettlementPair = (
  pendingDueRow?: UnadjustedEntryRow,
  creditAvailableRow?: UnadjustedEntryRow,
  partyType: ReferenceSettlementPartyType = "customer"
): string | null => {
  if (!pendingDueRow || !creditAvailableRow) {
    return null;
  }

  const pendingAmount = resolveOutstanding(pendingDueRow);
  const creditAmount = resolveOutstanding(creditAvailableRow);

  if (pendingAmount > creditAmount + 0.009) {
    if (partyType === "vendor") {
      return `Vendor credit (${formatMoney(pendingAmount)}) exceeds payment (${formatMoney(creditAmount)}). Select a payment reference with equal or higher value.`;
    }
    return `Pending due (${formatMoney(pendingAmount)}) exceeds credit available (${formatMoney(creditAmount)}). Select a credit reference with equal or higher value.`;
  }

  return null;
};

export const maxAllowedSettlement = (
  pendingDueRow?: UnadjustedEntryRow,
  creditAvailableRow?: UnadjustedEntryRow,
  partyType: ReferenceSettlementPartyType = "customer"
): number => {
  if (!pendingDueRow || !creditAvailableRow) {
    return 0;
  }
  if (validateSettlementPair(pendingDueRow, creditAvailableRow, partyType)) {
    return 0;
  }
  return Math.min(
    resolveOutstanding(pendingDueRow),
    resolveOutstanding(creditAvailableRow)
  );
};
